import React, {useEffect} from 'react';
import { useSelector } from 'react-redux';
import * as fn from '../../../helper/uiFunctions';
import * as commands from './printCommands'
import { PRINTER_TYPE_QR } from "./types";

//debug on browser: about://device-log
//reference for esc command: https://github.com/abdullahtariq91/thermal-printer-chrome-extension/blob/master/README.md
//reference for image printing: https://www.visuality.pl/posts/thermal-printer-protocols-for-image-and-text
const usePrintUsb = (deviceType, printType, serialNo) => {
    const serverAttributes = useSelector(state => state.auth.server);
    //read the vendor id supported by the app from server attributes
    const [devicesSupported, setDevicesSupported] = React.useState(null);
    const [autoPrintMsg, setAutoPrintMsg] = React.useState(null);
    const [callback, setCallback] = React.useState(null);

    const [deviceLanguage, setDeviceLanguage] = React.useState(commands.PRINT_LANG_ESC);
    const [isDevicePaired, setIsDevicePaired] = React.useState(false);
    const [isDeviceConnected, setIsDeviceConnected] = React.useState(false);
    // var port = 0;
    //this will be overwritten when the interface is claimed
    var endpoint = 0x01;

    const getDeviceBrands = () => {

        // console.log("supported devices 1", serverAttributes);
        let devices = devicesSupported && devicesSupported.brands;

        if(!devices && serverAttributes && serverAttributes.DeviceIntegrated){
            let devicesWithBrands = fn.findDeviceSupportedBrands(serverAttributes.DeviceIntegrated, deviceType, printType);
            // console.log("supported devices 2", devices && devices.brands);
            if (devicesWithBrands) {
                setDevicesSupported(devicesWithBrands);
                devices = devicesWithBrands.brands;
            }
        }

        //call auto print if available, this is to ensure that the first print happens 
        //if the calling parents want it even when extension is not ready
        if(devices && autoPrintMsg) {
            // console.log("report auto print triggered");
            sendDataToDevice(autoPrintMsg);
        }
    }

    useEffect(getDeviceBrands, [autoPrintMsg, serverAttributes, deviceType, printType]);

    const onDeviceConnected = function(e){
        // console.log("device connected", e, isDeviceConnected);
        checkDeviceConnection();
    }

    const onDeviceDisconnected = function(e){
        // console.log("device disconnected", e, isDeviceConnected);
        checkDeviceConnection();
    }

    const checkDeviceConnection = () => {
        navigator.usb.getDevices().then(devices => {
            // console.log("devices supported", devicesSupported, "devices found", devices.length, devices);

            if(devices && devices.length > 0){
                let deviceFound = fn.findDeviceRequired(devicesSupported, devices, serialNo);

                if(deviceFound){
                    setIsDevicePaired(true);
                    setIsDeviceConnected(true);

                    setDeviceLanguage(fn.findDeviceLanguage(devicesSupported, deviceFound.vendorId, deviceFound.productId, commands.PRINT_LANG_ESC));

                    return true;
                }
            }
        });

        setIsDevicePaired(false);
        setIsDeviceConnected(false);

        return false;
    }

    let initialiseDeviceManager = function() {
        let currentDevicePairedState = checkDeviceConnection();

        navigator.usb.addEventListener('connect', onDeviceConnected);
        navigator.usb.addEventListener('disconnect', onDeviceDisconnected);

        //call auto print if available, this is to ensure that the first print happens
        //if the calling parents want it even when extension is not ready
        if(currentDevicePairedState && autoPrintMsg) {
            // console.log("report auto print triggered");
            sendDataToDevice(autoPrintMsg);
        }

        // component unmount code
        return function () {
            navigator.usb.removeEventListener('connect', onDeviceConnected);
            navigator.usb.removeEventListener('disconnect', onDeviceDisconnected);
        }
    }

    useEffect(initialiseDeviceManager, [devicesSupported, autoPrintMsg]);
    
    const requestDevicePermission = () => {
        navigator.usb.requestDevice({ filters: devicesSupported.brands })
        .then(device => {
            // console.log("device permission", device);
            setIsDevicePaired(device ? true : false);
            setIsDeviceConnected(device ? true : false);

            if(device){
                setDeviceLanguage(fn.findDeviceLanguage(devicesSupported, device.vendorId, commands.PRINT_LANG_ESC));
            }
        })
        .catch(error => { console.error(error); });
    }

    const sendCallback = (type, msg) => {
        if(callback) {
            callback(type, msg);
        }
    }

    async function sendEscPayload(device, endpointNumber, data) {
        let i = 0;
        let pendingTransfers = [];
        let remainingBytes = data.byteLength;

        while (remainingBytes > 0) {
            const chunk = data.subarray(
                i * commands.BULK_TRANSFER_SIZE,
                (i + 1) * commands.BULK_TRANSFER_SIZE
            );

            // If we've reached max number of transfers, let's wait.
            if (pendingTransfers.length === commands.MAX_NUMBER_TRANSFERS) {
                await pendingTransfers.shift();
                // console.log("device print result", result);
            }

            // Submit transfers that will be executed in order.
            // console.log("device push chunk", remainingBytes);
            pendingTransfers.push(device.transferOut(endpointNumber, chunk));
            remainingBytes -= chunk.byteLength;

            i++;
        }

        // And wait for last remaining transfers to complete.
        // let result = await Promise.all(pendingTransfers);
        Promise.all(pendingTransfers)
        .catch(function(err) {
            // log that I have an error, return the entire array;
            console.log('device print error', err);
            sendCallback("error", "Error printing");
            return pendingTransfers;
        })
        .then(function(pendingTransfers) {
            sendCallback("ok", "Printing Finished");
            // console.log("device print result", pendingTransfers);
            // full array of resolved promises;
        });

        // console.log("device print result", result);
    }

    async function sendZplPayload(device, endpointNumber, data) {
        let pendingTransfers = [];

        for (let i = 0; i < data.length ; i++) {
            pendingTransfers.push(device.transferOut(endpointNumber, data[i]));
        }

        try {
            await Promise.all(pendingTransfers);
            sendCallback("ok", "Printing Finished");
        } catch (err) {
            console.log('device print error', err);
            sendCallback("error", "Error printing");
        }

        // console.log("device print result", result);
    }

    async function sendTSPLPayload(device, endpointNumber, data) {
        console.log("TSPL payload:", data);
        // return;

        let pendingTransfers = [];

        for (let i = 0; i < data.length ; i++) {
            pendingTransfers.push(device.transferOut(endpointNumber, data[i]));
        }

        try {
            await Promise.all(pendingTransfers);
            sendCallback("ok", "Printing Finished");
        } catch (err) {
            console.log('device print error', err);
            sendCallback("error", "Error printing");
        }

        // console.log("device print result", result);
    }

    async function openDevice(device) {
        console.log("opening device");
        await device.open();
    }

    async function selectDeviceConfiguration(device) {
        console.log("device configuration");
        await device.selectConfiguration(1);
    }

    async function resetDevice(device) {
        try
        {
            // console.log("device reset");
            await device.reset();
        }
        catch
        {
            
        }
    }

    async function claimDeviceInterface(device) {
        // console.log("device interface");
        //set the endpoint 
        endpoint = commands.getEndpointNumber(device.configuration.interfaces[0].alternate.endpoints, "out");
        await device.claimInterface(device.configuration.interfaces[0].interfaceNumber);
    }

    const sendDataToDevice = async (printData) => {
        //  commands.prepareData(printData, deviceLanguage);

        if (commands.isChrome()){
            if (isDevicePaired){
                try
                {
                    //get all device and then connect to the one that matches the print type
                    let devices = await navigator.usb.getDevices();
                    
                    let device = fn.findDeviceRequired(devicesSupported, devices, serialNo);

                    // console.log("supported devices 3", device);
                    // return;

                    await openDevice(device);
                    await selectDeviceConfiguration(device);
                    await resetDevice(device);
                    await claimDeviceInterface(device);

                    sendCallback("info", "printing...");
                    // console.log("printing", printData);

// if (deviceLanguage === commands.PRINT_LANG_TSPL) {
//     const testCmd = [
//             'SIZE 100 mm,25 mm',
//             'GAP 2 mm,0 mm',
//             'CLS',
//             'SPEED 3',
//             'DENSITY 6',
//             'TEXT 20,10,"2",0,1,1,"Ame-cz AAA B"',
//             'TEXT 20,30,"2",0,1,1,"ag 1.5 X 3"',
//             'QRCODE 20,60,L,5,A,0,"2019001951"',
//             'TEXT 290,10,"2",0,1,1,"Ame-cz AAA B"',
//             'TEXT 290,30,"2",0,1,1,"ag 1.5 X 3"',
//             'QRCODE 290,60,L,6,A,0,"2019001951"',
//             'TEXT 560,10,"2",0,1,1,"Ame-cz AAA B"',
//             'TEXT 560,30,"2",0,1,1,"ag 1.5 X 3"',
//             'QRCODE 560,60,L,5,A,0,"2019001951"',
//             'PRINT 1',
//             'END'
//     ].join('\r\n');
//     const testPayload = [new TextEncoder().encode(testCmd)];
//     console.log("Hardcoded TSPL payload:", testPayload);
//     await sendTSPLPayload(device, endpoint, testPayload);
//     return;
// }

                    if (printData.printType === PRINTER_TYPE_QR) {
                        if (deviceLanguage === commands.PRINT_LANG_ZPL) {
                            await sendZplPayload(device, endpoint, commands.prepareData(printData, deviceLanguage));
                        }
                        else if (deviceLanguage === commands.PRINT_LANG_TSPL) {
                            // await sendTestPayload(device, endpoint, commands.prepareData(printData, deviceLanguage));
                            await sendTSPLPayload(device, endpoint, commands.prepareData(printData, deviceLanguage));
                        }
                    }
                    else {
                        // console.log("Test printing on device", device);
                        //skip printing while testin, uncomment for production
                        await sendEscPayload(device, endpoint, commands.prepareData(printData, deviceLanguage));
                    }

                    //reset the auto print message
                    setAutoPrintMsg(null);
                }
                catch(err){
                    console.log("device printing error", err);
                }
            }
            else if(printData){
                setAutoPrintMsg(printData);
            }
        }
    }

    const returnObj = {
        isDevicePaired,
        isDeviceConnected,
        checkDeviceConnection,
        requestDevicePermission,
        sendData: sendDataToDevice,
        setCallback: setCallback
    }

    return returnObj;
}

export default usePrintUsb;
